import { useState, useEffect, useCallback } from 'react';

export type HealthStatus = 'online' | 'warning' | 'down' | 'loading' | 'error';

interface HealthData {
    LAST_CHECK_TIME: number;
    APPS_PROD: Record<string, { status: string }>;
    APPS_STAGING: Record<string, { status: string }>;
    DBS: Record<string, { status: string }>;
    SERVERS: Record<string, { status: string; verbose?: any }>;
    DNS: Record<string, { status: string; verbose?: any }>;
    APIS: Record<string, { status: string }>;
}

/**
 * Hook to monitor the technological provider's health.
 * Fetches data from Plemsi Watcher and aggregates the status.
 * @param {number} intervalMs - Polling interval in ms (default: 60000)
 */
export const useProviderHealth = (intervalMs = 60000) => {
    const [status, setStatus] = useState<HealthStatus>('loading');
    const [rawDetails, setRawDetails] = useState<HealthData | null>(null);
    const [lastError, setLastError] = useState<string | null>(null);

    const mapHealthStatus = useCallback((data: HealthData): HealthStatus => {
        if (!data) return 'loading';

        // Critical services for Production
        const criticalFields = [
            data.APPS_PROD?.Backend?.status,
            data.APPS_PROD?.API?.status,
            data.APIS?.Auth?.status,
            data.APIS?.DIAN?.status,
            data.APIS?.BillingBill?.status
        ];

        // If any critical service is down, the whole status is "down"
        if (criticalFields.some(s => s === 'down')) {
            return 'down';
        }

        // Warning services (non-critical but important)
        const warningFields = [
            data.DNS?.['plemsi.com']?.status,
            data.SERVERS?.Production?.status,
            data.APIS?.ReportStatics?.status,
            data.APIS?.BillingCredit?.status,
            data.APIS?.BillingDebit?.status
        ];

        // If any warning service is down, return "warning"
        if (warningFields.some(s => s === 'down')) {
            return 'warning';
        }

        return 'online';
    }, []);

    const fetchHealth = useCallback(async () => {
        try {
            const response = await fetch('https://watcher.plemsi.info/health/data/all');

            if (!response.ok) {
                throw new Error(`Watcher responded with status: ${response.status}`);
            }

            const data = await response.json();
            setRawDetails(data);
            setStatus(mapHealthStatus(data));
            setLastError(null);
        } catch (error: any) {
            console.error('Failed to fetch provider health:', error);
            setStatus('error');
            setLastError(error?.message || 'Error de conexión');
        }
    }, [mapHealthStatus]);

    useEffect(() => {
        // Initial fetch
        fetchHealth();

        // Setup polling
        const timer = setInterval(fetchHealth, intervalMs);

        return () => clearInterval(timer);
    }, [fetchHealth, intervalMs]);

    return {
        status,
        rawDetails,
        lastError,
        refresh: fetchHealth
    };
};
